import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { sendReferrerMessage } from "@/lib/referrer-messages.functions";

/**
 * Dialog pro odeslání zprávy/e-mailu doporučiteli přímo z popupu na mapě.
 * Odeslání se zároveň eviduje jako návštěva se zdrojem chat/e-mail, takže se
 * projeví v adminu Doporučení i ve statistikách aktivace na mapě.
 */
export function ReferrerMessageDialog({
  userId,
  lang,
  onClose,
}: {
  userId: string | null;
  lang?: string | null;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState<"message" | "email">("message");

  const send = useMutation({
    mutationFn: async () => {
      if (!userId) return { ok: false as const, reason: "no_target" as const };
      return sendReferrerMessage({
        data: {
          referrerUserId: userId,
          senderName: name.trim() || null,
          senderEmail: email.trim() || null,
          message: message.trim(),
          channel,
          source: channel === "email" ? "email" : "chat",
          lang: lang ?? null,
        },
      });
    },
    onSuccess: (res) => {
      if (!res?.ok) {
        toast.error("Zprávu se nepodařilo odeslat, zkuste to prosím později.");
        return;
      }
      toast.success("Zpráva byla odeslána doporučiteli.");
      setMessage("");
      onClose();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Odeslání selhalo"),
  });

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Napsat doporučiteli</DialogTitle>
          <DialogDescription>
            Zpráva dorazí členovi Direct Clubu i do administrace Doporučení.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="rm-name">Vaše jméno</Label>
            <Input id="rm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jan Novák" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rm-email">Váš e-mail (pro odpověď)</Label>
            <Input id="rm-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jan@example.com" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rm-msg">Zpráva</Label>
            <Textarea id="rm-msg" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={channel === "message" ? "default" : "outline"}
              onClick={() => setChannel("message")}
            >
              💬 Zpráva v aplikaci
            </Button>
            <Button
              type="button"
              size="sm"
              variant={channel === "email" ? "default" : "outline"}
              onClick={() => setChannel("email")}
            >
              ✉ Poslat i e-mailem
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Zrušit</Button>
          <Button disabled={message.trim().length < 3 || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Odesílám…" : "Odeslat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
